import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Search, FolderOpen, FileText, ChevronLeft, ChevronRight, Download } from "lucide-react";

const NIVEAU_LABELS = {
  PUBLIC: "Public", ADHERENT: "Adhérents", PRATICIEN: "Praticiens",
  SEMINAIRE_SPECIFIQUE: "Événement", ADMIN: "Admin",
};

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function MemberDocuments() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  const q = params.get("q") || "";
  const categorie = params.get("categorie_id") || "";
  const annee = params.get("annee") || "";
  const typeFichier = params.get("type_fichier") || "";
  const page = parseInt(params.get("page") || "1", 10);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page), page_size: "12" });
    if (q) qs.set("q", q);
    if (categorie) qs.set("categorie_id", categorie);
    if (annee) qs.set("annee", annee);
    if (typeFichier) qs.set("type_fichier", typeFichier);
    api(`/membre/documents?${qs}`).then(setData).catch(() => {});
  }, [q, categorie, annee, typeFichier, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api("/documents/categories").then(setCategories).catch(() => {}); }, []);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const annees = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <AppLayout>
      <div data-testid="documents-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <FolderOpen className="h-7 w-7 text-cfhtb-primary" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Base documentaire</h1>
            <p className="text-sm text-cfhtb-text">Documents mis à disposition selon vos droits d'accès.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-5 h-fit">
            <p className="text-xs uppercase tracking-wider font-medium text-cfhtb-text/80 mb-3">Catégories</p>
            <ul className="space-y-1" data-testid="doc-categories-list">
              <li>
                <button data-testid="doc-category-all" onClick={() => updateParams({ categorie_id: "" })}
                  className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${!categorie ? "bg-cfhtb-primary text-white font-medium" : "text-cfhtb-text hover:bg-cfhtb-bg"}`}>
                  Toutes les catégories
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`doc-category-${c.slug}`}
                    onClick={() => updateParams({ categorie_id: c.id })}
                    className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors flex justify-between items-center ${categorie === c.id ? "bg-cfhtb-primary text-white font-medium" : "text-cfhtb-text hover:bg-cfhtb-bg"}`}
                    style={c.parent_id ? { paddingLeft: "1.75rem" } : undefined}
                  >
                    <span>{c.nom}</span>
                    <span className={`text-xs ${categorie === c.id ? "text-white/80" : "text-cfhtb-text/60"}`}>{c.nb_documents}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="lg:col-span-3 space-y-4">
            <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 flex flex-wrap items-end gap-3">
              <form className="flex-1 min-w-[200px]" onSubmit={(e) => { e.preventDefault(); updateParams({ q: searchInput }); }}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input data-testid="doc-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Rechercher (titre, description, tags)…"
                    className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light" />
                </div>
              </form>
              <select data-testid="doc-filter-annee" value={annee} onChange={(e) => updateParams({ annee: e.target.value })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
                <option value="">Toutes années</option>
                {annees.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select data-testid="doc-filter-type" value={typeFichier} onChange={(e) => updateParams({ type_fichier: e.target.value })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
                <option value="">Tous formats</option>
                {["pdf", "docx", "xlsx", "pptx", "txt", "csv", "zip"].map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
            </div>

            {!data ? (
              <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
            ) : data.items.length === 0 ? (
              <div className="bg-white rounded-lg border border-cfhtb-light/30 p-10 text-center" data-testid="doc-empty">
                <FileText className="h-8 w-8 mx-auto text-cfhtb-light" />
                <p className="text-sm text-cfhtb-text mt-3">Aucun document ne correspond aux critères.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="doc-list">
                {data.items.map((d) => (
                  <Link
                    key={d.id}
                    to={`/documents/${d.id}`}
                    data-testid={`doc-card-${d.id}`}
                    className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 flex items-center gap-4 hover:shadow-md hover:border-cfhtb-light transition-all group"
                  >
                    <div className="h-11 w-11 rounded-md bg-cfhtb-bg flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-cfhtb-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-cfhtb-heading text-sm group-hover:text-cfhtb-primary transition-colors truncate">{d.titre || d.nom_fichier}</p>
                      <p className="text-xs text-cfhtb-text mt-0.5 truncate">{d.description}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-cfhtb-text/70">
                        <span className="uppercase font-semibold text-cfhtb-secondary">{d.extension}</span>
                        <span>{formatSize(d.taille)}</span>
                        <span>{formatDate(d.publie_le)}</span>
                        {d.categorie_nom && <span className="bg-cfhtb-bg px-2 py-0.5 rounded-full">{d.categorie_nom}</span>}
                        <span className="bg-cfhtb-primary/10 text-cfhtb-primary px-2 py-0.5 rounded-full font-medium">
                          {NIVEAU_LABELS[d.niveau_acces] || d.niveau_acces}
                        </span>
                      </div>
                    </div>
                    <Download className="h-4 w-4 text-cfhtb-light shrink-0 group-hover:text-cfhtb-primary transition-colors" />
                  </Link>
                ))}
              </div>
            )}

            {data && data.total > 0 && (
              <div className="flex items-center justify-center gap-3 pt-2">
                <button data-testid="doc-prev" disabled={page <= 1} onClick={() => updateParams({ page: String(page - 1) })}
                  className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-cfhtb-text" data-testid="doc-pagination-info">{data.total} document(s) — page {page} / {totalPages}</span>
                <button data-testid="doc-next" disabled={page >= totalPages} onClick={() => updateParams({ page: String(page + 1) })}
                  className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
